/** Karşılama penceresinin "kime, ne zaman" kararı.
 *
 * Pencerenin kendisi `/settings` yanıtındaki `welcome_popup` bloğudur (bkz.
 * backend/services/welcome_popup.py). O yanıt nginx'te önbelleklenir ve üyeye
 * de ziyaretçiye de aynı gider; bu yüzden kitle ve sıklık kararı **burada**
 * verilir. Karar saf fonksiyondur ki test edilebilsin.
 *
 * Gösterim izi tarayıcıdadır: "bir kez" ve "günde bir" `localStorage`'ta,
 * "oturumda bir" `sessionStorage`'ta durur. İz sürümü taşır — içerik panelde
 * değişince sürüm artar ve pencere kapatmış oyuncuya da yeniden görünür.
 */
export const WELCOME_POPUP_STORAGE_KEY = 'app:welcome-popup-v1';
export const WELCOME_POPUP_SESSION_KEY = 'app:welcome-popup-session-v1';

// Türkiye saati sabit UTC+3'tür; tarayıcının saat dilimine bağlanılmaz —
// yurt dışındaki oyuncunun "gün"ü başka bir saatte dönerdi.
const TR_OFFSET_MS = 3 * 60 * 60 * 1000;

export const turkeyDay = (now = Date.now()) => (
  new Date(now + TR_OFFSET_MS).toISOString().slice(0, 10)
);

const readJson = (storage, key) => {
  try {
    const raw = storage?.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeJson = (storage, key, value) => {
  try {
    storage?.setItem(key, JSON.stringify(value));
  } catch {
    // Gizli sekme ya da engellenmiş depolama: pencere yine gösterilir, yalnızca
    // bir sonraki açılışta hatırlanmaz.
  }
};

const safeStorage = (name) => {
  try {
    return typeof window !== 'undefined' ? window[name] : null;
  } catch {
    return null;
  }
};

export const readWelcomeSeen = () => ({
  seen: readJson(safeStorage('localStorage'), WELCOME_POPUP_STORAGE_KEY),
  sessionSeen: readJson(safeStorage('sessionStorage'), WELCOME_POPUP_SESSION_KEY),
});

export const markWelcomeSeen = (popup, now = Date.now()) => {
  const version = Number(popup?.version) || 0;
  writeJson(safeStorage('localStorage'), WELCOME_POPUP_STORAGE_KEY,
    { version, day: turkeyDay(now) });
  writeJson(safeStorage('sessionStorage'), WELCOME_POPUP_SESSION_KEY, { version });
};

const isHome = (pathname) => pathname === '/' || pathname === '';

/** Pencere bu ziyaretçiye, bu sayfada gösterilsin mi? */
export const shouldShowWelcomePopup = (popup, {
  user = null, pathname = '/', seen = null, sessionSeen = null, now = Date.now(),
} = {}) => {
  if (!popup || typeof popup !== 'object') return false;
  if (!(popup.title || popup.body || popup.image_url)) return false;
  const path = String(pathname || '/');
  if (path.startsWith('/admin')) return false;
  if ((popup.pages || 'home') === 'home' && !isHome(path)) return false;
  const audience = popup.audience || 'all';
  if (audience === 'guest' && user) return false;
  if (audience === 'member' && !user) return false;
  const version = Number(popup.version) || 0;
  switch (popup.frequency || 'once') {
    case 'always':
      return true;
    case 'session':
      return !(sessionSeen && Number(sessionSeen.version) === version);
    case 'daily':
      return !(seen && Number(seen.version) === version && seen.day === turkeyDay(now));
    case 'once':
    default:
      return !(seen && Number(seen.version) === version);
  }
};

/** Düğme bağlantısı site içi mi (router ile açılır) yoksa dış adres mi. */
export const isInternalLink = (url) => (
  typeof url === 'string' && url.startsWith('/') && !url.startsWith('//')
);
